import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MoreHorizontal, Paperclip, Download } from "lucide-react";
import Avatar from "../../components/common/Avatar";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../../components/common/FifaVerifiedBadge";
import SearchRequestStatusChip from "../../components/club-network/SearchRequestStatusChip";
import CloseSearchRequestModal from "../../components/club-network/CloseSearchRequestModal";
import WithdrawSearchRequestModal from "../../components/club-network/WithdrawSearchRequestModal";
import RequestAttribution from "../../components/club-network/RequestAttribution";
import ActivityTrail from "../../components/club-network/ActivityTrail";
import SpecRequirements from "../../components/club-network/SpecRequirements";
import { usePermission } from "../../hooks/usePermission";
import { trustedAgentService } from "../../services/clubNetworkApi";
import {
  formatRequestTitle,
  formatSecondary,
  formatTerms,
  formatSentDate,
  isPendingOnly,
  requestHasWinner,
} from "../../utils/searchRequestFormat";
import {
  formatTypeOfTender,
  formatLeagueTier,
} from "../../constants/agentTenderChoices";

// N3 search-request detail (design N3·4 "View"). The full spec the club sent,
// plus every recipient with its status — accepted opens the chat, declined shows
// the agent's optional reason. Close is the same soft transition as the list,
// behind the platform overflow + destructive confirm (CloseSearchRequestModal).
const MetaChip = ({ children }) =>
  children ? (
    <span className="text-[11.5px] font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
      {children}
    </span>
  ) : null;

const RecipientRow = ({ recipient, onOpenChat }) => {
  const { status, decline_reason: reason, agent } = recipient;
  return (
    <div className="flex items-center gap-3 py-3 border-t border-[#F3F4F6] first:border-t-0">
      <Avatar src={agent?.avatar} fallbackText={agent?.display_name} size={34} />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="text-[13.5px] font-semibold text-[#111827] truncate">
            {agent?.display_name}
          </span>
          {/* FIFA badge replaced the old "Licensed" chip; gate is
              isFifaVerified (Marin 2026-08-04: self-declaration carries
              the badge, only failed_qr hides it). */}
          {isFifaVerified(agent) && <FifaVerifiedBadge size="sm" />}
          <SearchRequestStatusChip status={status} />
        </span>
        {agent?.agency_name && (
          <span className="text-[11.5px] text-gray-500 truncate">{agent.agency_name}</span>
        )}
        {status === "declined" && reason && (
          <span className="text-[12px] italic text-gray-400 mt-0.5">“{reason}”</span>
        )}
      </div>
      {status === "accepted" && (
        <button
          onClick={() => onOpenChat(recipient)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-[12.5px] font-semibold hover:bg-gray-50 flex-none"
        >
          Open chat
        </button>
      )}
    </div>
  );
};

const SearchRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canClose = usePermission("delete_tenders");
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    trustedAgentService.getRequest(id).then((res) => {
      if (cancelled) return;
      if (res.success) setRequest(res.data);
      else setError(res.error || "Request not found.");
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const openChat = (recipient) => {
    if (recipient.chat_channel_id) navigate(`/inbox?channel=${recipient.chat_channel_id}`);
    else navigate("/inbox");
  };

  const confirmClose = async () => {
    setBusy(true);
    const res = await trustedAgentService.closeRequest(request.id);
    setBusy(false);
    setClosing(false);
    if (res.success) setRequest((prev) => ({ ...prev, status: "closed" }));
  };

  const confirmWithdraw = async () => {
    setBusy(true);
    const res = await trustedAgentService.withdrawRequest(request.id);
    setBusy(false);
    setWithdrawing(false);
    if (res.success) setRequest((prev) => ({ ...prev, status: "closed" }));
  };

  const openDocument = async () => {
    setDocBusy(true);
    const res = await trustedAgentService.getDocumentUrl(request.id);
    setDocBusy(false);
    if (res.success && res.data?.download_url) {
      window.open(res.data.download_url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-500">Loading…</div>;
  }
  if (error || !request) {
    return (
      <div className="flex flex-col items-center py-20 text-center gap-3">
        <p className="text-red-500">{error || "Request not found."}</p>
        <button
          onClick={() => navigate("/network/trusted-agent")}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Back to my search requests
        </button>
      </div>
    );
  }

  const secondary = formatSecondary(request);
  const terms = formatTerms(request);
  const sent = formatSentDate(request);
  const isClosed = request.status === "closed";
  const pendingOnly = isPendingOnly(request);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/network/trusted-agent")}
          className="text-[12.5px] font-semibold text-[#2283FF] mb-4"
        >
          ‹ Back to my search requests
        </button>

        <div className="bg-white border border-[#D6DBE3] rounded-xl p-6 sm:p-8 flex flex-col gap-5">
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-[#111827]">
                {formatRequestTitle(request)}
                {secondary && (
                  <span className="font-normal text-gray-500 text-[14px]"> · also {secondary}</span>
                )}
                {isClosed && (
                  <span className="ml-2 text-[12px] font-semibold text-gray-400 uppercase align-middle">
                    Closed
                  </span>
                )}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <MetaChip>{formatTypeOfTender(request.transfer_type)}</MetaChip>
                <MetaChip>{request.recruiting_for_display}</MetaChip>
                <MetaChip>{request.window}</MetaChip>
                {request.level_expectation && (
                  <MetaChip>{`${formatLeagueTier(request.level_expectation)} level`}</MetaChip>
                )}
              </div>
              {(request.created_by || (isClosed && request.closed_by)) && (
                <RequestAttribution
                  createdBy={request.created_by}
                  createdAt={request.created_at}
                  closedBy={isClosed ? request.closed_by : null}
                  closedAt={request.closed_at}
                />
              )}
            </div>
            {!isClosed && canClose && (
              <div className="relative flex-none" ref={menuRef}>
                <button
                  type="button"
                  aria-label="Request actions"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-9 bg-white py-1.5 shadow-lg border border-gray-100 rounded-lg z-10 min-w-[170px]">
                    {pendingOnly ? (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          setWithdrawing(true);
                        }}
                        className="w-full text-left px-3 py-2 text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
                      >
                        Withdraw invitation
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          setClosing(true);
                        }}
                        className="w-full text-left px-3 py-2 text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
                      >
                        Close request
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {request.additional_details && (
            <p className="text-[13.5px] text-[#374151] leading-relaxed bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-4">
              {request.additional_details}
            </p>
          )}

          {terms && (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Terms offered
              </span>
              <span className="text-[13px] text-[#4B5563] bg-[#F3F4F6] px-3 py-2 rounded-lg self-start">
                {terms}
              </span>
            </div>
          )}

          <SpecRequirements request={request} />

          {request.has_document && (
            <button
              onClick={openDocument}
              disabled={docBusy}
              className="inline-flex items-center gap-2 self-start bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[13px] font-semibold text-[#111827] hover:bg-gray-50 disabled:opacity-50"
            >
              <Paperclip className="w-4 h-4 text-[#2283FF]" />
              Attached document
              <Download className="w-4 h-4 text-gray-400" />
              {docBusy && <span className="text-[12px] text-gray-400">Opening…</span>}
            </button>
          )}

          <div className="flex flex-col gap-0">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              {sent ? `Sent ${sent} to ${request.recipients?.length || 0} agent${
                (request.recipients?.length || 0) === 1 ? "" : "s"
              }` : "Recipients"}
            </span>
            {request.recipients?.map((r) => (
              <RecipientRow key={r.id} recipient={r} onOpenChat={openChat} />
            ))}
          </div>

          {request.activity_trail?.length > 0 && (
            <div className="border-t border-[#F3F4F6] pt-5">
              <ActivityTrail events={request.activity_trail} />
            </div>
          )}
        </div>
      </div>

      {closing && (
        <CloseSearchRequestModal
          onCancel={() => setClosing(false)}
          onConfirm={confirmClose}
          busy={busy}
          running={requestHasWinner(request)}
        />
      )}
      {withdrawing && (
        <WithdrawSearchRequestModal
          onCancel={() => setWithdrawing(false)}
          onConfirm={confirmWithdraw}
          busy={busy}
        />
      )}
    </div>
  );
};

export default SearchRequestDetail;
